/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, useMultiStyleConfig} from '@chakra-ui/react'
import {MenuCardGroup} from './menu-card-group'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Link as RouteLink} from 'react-router-dom'
import ChevronRightIcon from '../icons/arrows/ChevronRight'
import TertiaryLink from './tertiary-link'

const ListMenuContent = ({secondLevel, categoriesData, categoryUrl, onItemClick}) => {
    const styles = useMultiStyleConfig('ListMenu')

    let content = null
    if (secondLevel?.layout === 'Category') {
        content = (
            <Flex {...styles.categoryContainer}>
                <MenuCardGroup
                    group={secondLevel.standard_group[0]}
                    categoriesData={categoriesData}
                    onItemClick={onItemClick}
                />
                <MenuCardGroup
                    group={secondLevel.filters_group[0]}
                    categoriesData={categoriesData}
                    type="filter"
                    onItemClick={onItemClick}
                />
            </Flex>
        )
    }
    if (secondLevel?.layout === 'Sub category') {
        content = (
            <Flex {...styles.subCategoryContainer}>
                {secondLevel.sub_categories_group.map((group) => {
                    return (
                        <MenuCardGroup
                            key={group.uid}
                            group={group}
                            categoriesData={categoriesData}
                            type="subCategory"
                            onItemClick={onItemClick}
                        />
                    )
                })}
            </Flex>
        )
    }
    if (secondLevel?.layout === 'Promo/Gift') {
        content = (
            <Box {...styles.promoContainer}>
                <MenuCardGroup
                    group={secondLevel.promos_group[0]}
                    categoriesData={categoriesData}
                    type="promo"
                    onItemClick={onItemClick}
                />
            </Box>
        )
    }

    const tertiaryLinks = [
        secondLevel?.footer?.tertiary_link_1[0],
        secondLevel?.footer?.tertiary_link_2[0],
        secondLevel?.footer?.tertiary_link_3[0],
    ].filter((link) => link)

    return (
        <Flex {...styles.contentContainer}>
            {content}
            <Flex {...styles.footerContainer}>
                {tertiaryLinks.map((link) => {
                    return (
                        <TertiaryLink
                            key={link.uid}
                            link={link}
                            categoriesData={categoriesData}
                            onClick={onItemClick}
                        />
                    )
                })}

                {secondLevel?.footer?.show_explore_all && (
                    <Link
                        as={RouteLink}
                        to={categoryUrl}
                        {...styles.exploreAllButton}
                        onClick={onItemClick}
                    >
                        {secondLevel.footer?.explore_all_label}
                        <ChevronRightIcon boxSize="iconsSize.small" />
                    </Link>
                )}
            </Flex>
        </Flex>
    )
}

ListMenuContent.propTypes = {
    secondLevel: PropTypes.object,
    categoriesData: PropTypes.object,
    categoryUrl: PropTypes.string,
    onItemClick: PropTypes.func,
}

export {ListMenuContent}
